import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { Search, Check, MessageCircle, ShieldCheck, Clock, ChevronDown } from "lucide-react";

export const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];
const ROLES = ["Teacher", "Headmaster", "MDM In-charge", "CRCC", "BEO", "Other"];

const BENEFITS = [
  { icon: "⚙️", en: "Your state's official rates", hi: "आपके राज्य की दरें", or: "ଆପଣଙ୍କ ରାଜ୍ୟର ଦର" },
  { icon: "🍛", en: "Your state's weekly menu", hi: "आपके राज्य का मेन्यू", or: "ଆପଣଙ୍କ ରାଜ୍ୟର ମେନୁ" },
  { icon: "📄", en: "MDCF & reports in your format", hi: "आपके फॉर्मेट में MDCF", or: "ଆପଣଙ୍କ ଫର୍ମାଟରେ MDCF" },
  { icon: "🗣️", en: "In your language", hi: "आपकी भाषा में", or: "ଆପଣଙ୍କ ଭାଷାରେ" },
];

function buildMessage(lang, state, district, name, role) {
  const d = district || "—", n = name || "—", r = role || "—";
  if (lang === "or") {
    return `🙏 ନମସ୍କାର Admin,\nମୁଁ ମୋ ରାଜ୍ୟ ପାଇଁ PM POSHAN MDM କ୍ୟାଲକୁଲେଟର ଚାହୁଁଛି।\n\n📍 ରାଜ୍ୟ: ${state}\n🏙️ ଜିଲ୍ଲା: ${d}\n👤 ନାମ: ${n}\n💼 ପଦବୀ: ${r}\n\nଦୟାକରି ବିବରଣୀ ଦିଅନ୍ତୁ। ଧନ୍ୟବାଦ!\n(Sent from pmposhanmdmcalculator.in)`;
  }
  return `🙏 Namaste Admin,\nमुझे मेरे राज्य के लिए PM POSHAN MDM Calculator चाहिए।\n\n📍 State: ${state}\n🏙️ District: ${d}\n👤 Name: ${n}\n💼 Role: ${r}\n\nकृपया विवरण साझा करें। धन्यवाद!\n(Sent from pmposhanmdmcalculator.in)`;
}

export default function StateRequestSection() {
  const { lang } = useLang();
  const isOr = lang === "or";
  const [cfg, setCfg] = useState({ enabled: true, whatsapp: "8093414633", coming_soon: [], headline_en: "", headline_hi: "", headline_or: "", subline: "" });
  const [query, setQuery] = useState("");
  const [state, setState] = useState("");
  const [openList, setOpenList] = useState(false);
  const [district, setDistrict] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [err, setErr] = useState(false);
  const [pulse, setPulse] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const viewedRef = useRef(false);

  useEffect(() => { api.get("/public/config").then(({ data }) => { if (data.state_request) setCfg((c) => ({ ...c, ...data.state_request })); }).catch(() => {}); }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver((ents) => {
      ents.forEach((e) => {
        if (e.isIntersecting && !viewedRef.current) {
          viewedRef.current = true;
          track("state_section_viewed", {});
          setPulse(true);
          setTimeout(() => setPulse(false), 2600);
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (listRef.current && !listRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => STATES.filter((s) => s.toLowerCase().includes(query.toLowerCase())), [query]);
  const comingSoon = (cfg.coming_soon || []).map((x) => String(x).trim()).filter(Boolean);

  if (cfg.enabled === false) return null;

  const headline = isOr
    ? (cfg.headline_or || "ଆପଣଙ୍କ ରାଜ୍ୟ ପାଇଁ ମଧ୍ୟ MDM କ୍ୟାଲକୁଲେଟର ଚାହୁଁଛନ୍ତି କି?")
    : (cfg.headline_en || "Want this MDM Calculator for YOUR STATE? 🇮🇳");
  const headlineHi = cfg.headline_hi || "क्या आपके राज्य के लिए भी MDM कैलकुलेटर चाहिए?";
  const subline = cfg.subline || (isOr
    ? "ଆପଣଙ୍କ ରାଜ୍ୟର ଦର, ମେନୁ, ଶ୍ରେଣୀ ଓ MDCF ଫର୍ମାଟରେ ତିଆରି। ଓଡ଼ିଶାର ଶିକ୍ଷକଙ୍କ ପରି ପ୍ରତି ମାସ ଘଣ୍ଟା ବଞ୍ଚାନ୍ତୁ।"
    : "Your state's rates, menu, class groups and MDCF format, built for you. Save hours every month, just like teachers in Odisha.");

  const pickState = (s) => {
    setState(s); setQuery(s); setOpenList(false); setErr(false);
    track("state_selected", { state: s });
  };

  const onRequest = () => {
    if (!state) { setErr(true); setOpenList(true); if (listRef.current) listRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    track("state_whatsapp_tap", { state, role: role || "", district: district || "" });
    const num = String(cfg.whatsapp || "8093414633").replace(/\D/g, "");
    const msg = buildMessage(lang, state, district, name, role);
    window.open(`https://wa.me/91${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <section id="state-request" ref={rootRef} className="max-w-6xl mx-auto px-4 py-10 md:py-14" data-testid="state-request-section">
      <style>{`
        @keyframes srShine { 0%,88%,100%{background-position:-120% 0} 94%{background-position:220% 0} }
        @keyframes srPulse { 0%{box-shadow:0 0 0 0 rgba(37,211,102,.55)} 70%{box-shadow:0 0 0 14px rgba(37,211,102,0)} 100%{box-shadow:0 0 0 0 rgba(37,211,102,0)} }
        .sr-shine{background-image:linear-gradient(110deg,transparent 30%,rgba(255,255,255,.65) 48%,transparent 66%);background-size:220% 100%;background-repeat:no-repeat;animation:srShine 5s linear infinite}
        .sr-pulse{animation:srPulse 1.3s ease-out 2}
        @media (prefers-reduced-motion: reduce){.sr-shine,.sr-pulse{animation:none}}
      `}</style>
      <div className="relative overflow-hidden rounded-[24px] p-6 md:p-10 text-white shadow-warm"
        style={{ background: "linear-gradient(135deg,#053D21 0%,#0B6B3A 100%)" }}>
        {/* India map outline (decorative, low opacity) */}
        <svg viewBox="0 0 300 320" className="absolute right-[-30px] top-[-10px] w-[320px] h-[340px] pointer-events-none" aria-hidden="true" style={{ opacity: 0.07 }}>
          <path fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinejoin="round"
            d="M70 40 L110 30 L150 45 L165 35 L180 55 L210 60 L230 85 L215 110 L235 130 L225 160 L240 185 L215 205 L205 235 L185 255 L170 300 L150 260 L140 230 L120 215 L95 220 L80 195 L100 170 L85 150 L95 120 L75 100 L90 75 Z" />
        </svg>

        <div className="relative grid md:grid-cols-2 gap-8 items-start">
          {/* Left: text + benefits */}
          <div>
            <span className="sr-shine inline-flex items-center gap-1 rounded-full bg-thali-turmeric text-thali-ink font-bold text-xs px-3 py-1 mb-4" data-testid="state-badge">🆕 NOW FOR ALL STATES</span>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl leading-tight">
              {headline.split("YOUR STATE").map((part, i, arr) => (
                <React.Fragment key={i}>{part}{i < arr.length - 1 && <span className="text-thali-turmeric">YOUR STATE</span>}</React.Fragment>
              ))}
            </h2>
            {!isOr && <p className="font-heading font-bold text-lg md:text-xl text-white/90 mt-1">{headlineHi}</p>}
            <p className="text-white/80 mt-3 text-sm md:text-base max-w-xl">{subline}</p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
              {BENEFITS.map((b, i) => (
                <div key={i} className="bg-white text-thali-ink rounded-2xl p-3 flex flex-col gap-1 shadow-sm" data-testid={`state-benefit-${i}`}>
                  <span className="text-2xl">{b.icon}</span>
                  <span className="text-xs font-semibold leading-snug">{isOr ? b.or : b.en}</span>
                </div>
              ))}
            </div>

            {comingSoon.length > 0 && (
              <div className="mt-5" data-testid="state-coming-soon">
                <div className="text-xs text-white/70 mb-1 font-semibold">In progress:</div>
                <div className="flex flex-wrap gap-2">
                  {comingSoon.map((s) => <span key={s} className="text-xs bg-white/15 rounded-full px-3 py-1">🛠️ {s} — coming soon</span>)}
                </div>
              </div>
            )}
          </div>

          {/* Right: form + CTA */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 md:p-5 border border-white/15">
            <label className="text-sm font-semibold text-white/90">Choose your state</label>
            <div className="relative mt-1" ref={listRef}>
              <div className={`flex items-center gap-2 bg-white rounded-xl px-3 min-h-[48px] ${err ? "ring-2 ring-thali-turmeric" : ""}`}>
                <Search className="w-4 h-4 text-thali-ink/40 shrink-0" />
                <input value={query} onChange={(e) => { setQuery(e.target.value); setOpenList(true); setState(""); }} onFocus={() => setOpenList(true)}
                  placeholder="Search state / UT…" className="flex-1 bg-transparent text-thali-ink outline-none text-sm py-2" data-testid="state-search-input" />
                <ChevronDown className="w-4 h-4 text-thali-ink/40 shrink-0" onClick={() => setOpenList((o) => !o)} />
              </div>
              {openList && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto bg-white rounded-xl shadow-xl border border-thali-turmeric/20 text-thali-ink" data-testid="state-list">
                  {filtered.length === 0 && <div className="px-3 py-3 text-sm text-thali-ink/50">No match</div>}
                  {filtered.map((s) => (
                    <button key={s} onClick={() => pickState(s)} className={`w-full text-left px-3 py-2.5 text-sm hover:bg-thali-cream flex items-center justify-between ${state === s ? "bg-thali-cream font-semibold" : ""}`} data-testid={`state-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                      <span>{s}</span>
                      {s === "Odisha" && <span className="text-xs text-thali-green font-bold">✅ Already live</span>}
                      {comingSoon.includes(s) && <span className="text-xs text-thali-saffron font-bold">🛠️ Coming soon</span>}
                    </button>
                  ))}
                </div>
              )}
              {err && <div className="text-xs text-thali-turmeric font-bold mt-1" data-testid="state-error">Please choose your state</div>}
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="bg-white rounded-xl px-3 min-h-[44px] text-thali-ink text-sm outline-none col-span-2" data-testid="state-name" />
              <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-white rounded-xl px-3 min-h-[44px] text-thali-ink text-sm outline-none" data-testid="state-role">
                <option value="">Your role</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="District" className="bg-white rounded-xl px-3 min-h-[44px] text-thali-ink text-sm outline-none" data-testid="state-district" />
            </div>

            <button onClick={onRequest} className={`w-full mt-4 h-[60px] rounded-2xl font-bold text-white flex items-center justify-center gap-2 text-base ${pulse ? "sr-pulse" : ""}`}
              style={{ background: "#25D366" }} data-testid="state-whatsapp-btn">
              <MessageCircle className="w-5 h-5" /> 💬 Request on WhatsApp
            </button>
            <div className="text-center text-xs text-white/80 mt-2">Chat directly with the admin · +91 {String(cfg.whatsapp || "8093414633").replace(/\D/g, "").replace(/(\d{5})(\d{5})/, "$1 $2")}</div>
            <Link to="/calculator-builder" className="mt-3 w-full min-h-[48px] rounded-2xl font-bold flex items-center justify-center gap-2 text-sm bg-white text-thali-green hover:bg-thali-cream transition-colors" data-testid="state-builder-btn">
              🛠️ {isOr ? "କିମ୍ବା ନିଜେ ମାଗଣାରେ ତିଆରି କରନ୍ତୁ →" : "Or build it yourself — free, no code →"}
            </Link>
            <div className="text-center text-[11px] text-white/70 mt-2 flex items-center justify-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Reply within 24 hours</span>
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Shared only with the admin</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
